import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
  useStore,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const LAYER_COLOR = {
  COMPUTE:  '#3b82f6',
  IDENTITY: '#8b5cf6',
  NETWORK:  '#10b981',
  DATABASE: '#f59e0b',
  CODE:     '#ec4899',
  ENDPOINT: '#06b6d4',
};

const ASSET_ICON = {
  EC2_INSTANCE:    'fa-server',
  IAM_USER:        'fa-user',
  IAM_ROLE:        'fa-user-shield',
  S3_BUCKET:       'fa-archive',
  RDS_INSTANCE:    'fa-database',
  VPC:             'fa-network-wired',
  SECURITY_GROUP:  'fa-shield-alt',
  CODE_REPO:       'fa-code-branch',
  LAMBDA:          'fa-bolt',
  SERVICE_ACCOUNT: 'fa-robot',
  DEVICE:          'fa-laptop',
};

const EDGE_COLOR = { ASSUMES_ROLE: '#8b5cf6' };
const RISK_BANDS = [
  { key: 'critical', min: 80, color: '#dc2626' },
  { key: 'high',     min: 50, color: '#ea580c' },
  { key: 'medium',   min: 25, color: '#d97706' },
  { key: 'low',      min: 0,  color: '#16a34a' },
];

// Pod grid used inside every box.
const CELL_W = 132, CELL_H = 108, GAP = 8, PAD = 18, HEAD = 50, COL_GAP = 110, MAX_ROW_W = 1500;
const POD = 52;
// Below this zoom the labels are unreadable, so pods and box titles switch to a bigger, label-free look.
const FAR_ZOOM = 0.6;
const farSelector = s => s.transform[2] < FAR_ZOOM;
const NO_VPC = '__no_vpc__';
const BAND_IDENTITY = '__band_identity__';
const BAND_SERVICES = '__band_services__';

const riskColor = r => (RISK_BANDS.find(b => (r || 0) >= b.min) || RISK_BANDS[3]).color;
const prettyType = t => (t || '').replace(/_/g, ' ').toLowerCase();
const byRisk = (a, b) => (b.data?.riskScore || 0) - (a.data?.riskScore || 0);

// Keyframes and hover effects the inline styles cannot express.
const GRAPH_CSS = `
@keyframes ag-pulse { 0% { transform: scale(.85); opacity: .55 } 70% { transform: scale(1.35); opacity: 0 } 100% { opacity: 0 } }
.ag-pod { transition: transform .15s ease, opacity .2s ease; cursor: pointer }
.ag-pod:hover { transform: translateY(-2px) }
.ag-pod:hover .ag-disc { box-shadow: 0 8px 22px rgba(37,99,235,.22) }
.ag-flow .react-flow__edge-path { filter: drop-shadow(0 0 3px rgba(139,92,246,.35)) }
.ag-hot .react-flow__edge-path { filter: drop-shadow(0 0 4px rgba(220,38,38,.55)) }
.ag-canvas .react-flow__minimap { border-radius: 12px; overflow: hidden; box-shadow: 0 6px 24px rgba(15,23,42,.12) }
.ag-canvas .react-flow__controls { border-radius: 10px; overflow: hidden; box-shadow: 0 6px 20px rgba(15,23,42,.12) }
.ag-chip:hover { box-shadow: 0 2px 8px rgba(15,23,42,.1) }
.ag-row:hover { background: #f1f5f9 }
.ag-tool:hover:not(:disabled) { background: #f1f5f9 !important }
`;

function gridSize(count, maxCols) {
  const cols = Math.max(1, Math.min(maxCols, count));
  const rows = Math.max(1, Math.ceil(count / cols));
  return { cols, w: cols * CELL_W + (cols - 1) * GAP, h: count ? rows * CELL_H + (rows - 1) * GAP : 28 };
}

function countIn(c) {
  return Object.values(c.sgs).reduce((n, l) => n + l.length, 0) + c.loose.length;
}

/**
 * Places assets inside their security group, security groups inside their VPC, identities and
 * services in their own columns. Returns React Flow nodes (parents before children), the parent of
 * every asset, and the edges that still need a line (the others are shown by the boxes).
 */
function buildLayout(rawNodes, rawEdges) {
  const byId = Object.fromEntries(rawNodes.map(n => [String(n.id), n]));
  const type = id => byId[id]?.data?.assetType;
  const vpcOf = {}, sgsOf = {}, sgVpc = {};
  rawEdges.forEach(e => {
    const t = e.data?.edgeType;
    const s = String(e.source), d = String(e.target);
    if (t === 'DEPLOYED_ON' && type(d) === 'VPC') vpcOf[s] = vpcOf[s] || d;
    else if (t === 'PROTECTED_BY' && type(d) === 'SECURITY_GROUP') (sgsOf[s] = sgsOf[s] || []).push(d);
    else if (t === 'BELONGS_TO' && type(d) === 'VPC') sgVpc[s] = d;
  });

  const vpcs = rawNodes.filter(n => type(String(n.id)) === 'VPC');
  const sgs = rawNodes.filter(n => type(String(n.id)) === 'SECURITY_GROUP');
  const assets = rawNodes.filter(n => !['VPC', 'SECURITY_GROUP'].includes(type(String(n.id))));

  const assetSg = {};
  assets.forEach(a => { const sg = (sgsOf[a.id] || []).find(id => byId[id]); if (sg) assetSg[a.id] = sg; });
  // A security group without BELONGS_TO takes the VPC of its assets.
  sgs.forEach(s => {
    if (sgVpc[s.id]) return;
    const a = assets.find(x => assetSg[x.id] === String(s.id) && vpcOf[x.id]);
    if (a) sgVpc[s.id] = vpcOf[a.id];
  });

  const vpcOfSg = sg => (sgVpc[sg] && byId[sgVpc[sg]] ? sgVpc[sg] : NO_VPC);
  const containers = {};
  const ensure = v => (containers[v] = containers[v] || { sgs: {}, loose: [] });
  vpcs.forEach(v => ensure(String(v.id)));
  sgs.forEach(s => { ensure(vpcOfSg(String(s.id))).sgs[s.id] = []; });

  const identity = [], services = [];
  const parentOf = {};
  assets.forEach(a => {
    const id = String(a.id);
    const sg = assetSg[id];
    if (sg) containers[vpcOfSg(sg)].sgs[sg].push(a);
    else if (vpcOf[id] && byId[vpcOf[id]]) containers[vpcOf[id]].loose.push(a);
    else if (a.data?.layer === 'IDENTITY') identity.push(a);
    else services.push(a);
  });

  const out = [];
  const podsInto = (parentId, list, maxCols) => {
    const cols = Math.max(1, Math.min(maxCols, list.length));
    [...list].sort(byRisk).forEach((a, i) => {
      parentOf[a.id] = parentId;
      out.push({
        id: String(a.id), type: 'assetPod', parentId, extent: 'parent', draggable: false, width: CELL_W, height: CELL_H,
        position: { x: PAD + (i % cols) * (CELL_W + GAP), y: HEAD + Math.floor(i / cols) * (CELL_H + GAP) },
        data: a.data,
      });
    });
  };

  // Column 1: VPCs (and security groups without a VPC), biggest first.
  const order = Object.keys(containers)
    .filter(v => v !== NO_VPC || Object.keys(containers[v].sgs).length || containers[v].loose.length)
    .sort((a, b) => (a === NO_VPC) - (b === NO_VPC) || countIn(containers[b]) - countIn(containers[a]));
  const blocks = order.map(v => {
    const c = containers[v];
    const boxes = Object.entries(c.sgs)
      .map(([sgId, list]) => ({ id: sgId, kind: 'sg', list, ...gridSize(list.length, 4) }))
      .sort((a, b) => b.list.length - a.list.length || byRisk(byId[a.id], byId[b.id]));
    if (c.loose.length) boxes.push({ id: `${v}__loose`, kind: 'loose', list: c.loose, ...gridSize(c.loose.length, 4) });

    const placed = [];
    let x = 0, rowY = 0, rowH = 0, innerW = 0;
    boxes.forEach(b => {
      const bw = b.w + 2 * PAD, bh = HEAD + b.h + PAD;
      if (x > 0 && x + bw > MAX_ROW_W) { rowY += rowH + 16; x = 0; rowH = 0; }
      placed.push({ ...b, x, y: rowY, bw, bh });
      x += bw + 16; rowH = Math.max(rowH, bh); innerW = Math.max(innerW, x - 16);
    });
    return { v, c, placed, vw: Math.max(innerW, CELL_W * 2) + 2 * PAD, vh: HEAD + (boxes.length ? rowY + rowH : 28) + PAD };
  });

  // VPCs flow left to right and wrap at the width of the widest one, so many small VPCs do not
  // stack into one tall column.
  const rowLimit = Math.max(1100, ...blocks.map(b => b.vw));
  let px = 0, py = 0, shelfH = 0, colW = 0;
  blocks.forEach(({ v, c, placed, vw, vh }) => {
    if (px > 0 && px + vw > rowLimit) { px = 0; py += shelfH + 48; shelfH = 0; }
    const vpcNode = byId[v];
    out.push({
      id: v, type: 'groupBox', position: { x: px, y: py }, draggable: false,
      width: vw, height: vh, style: { width: vw, height: vh },
      data: { kind: v === NO_VPC ? 'novpc' : 'vpc', label: vpcNode ? vpcNode.data.label : null,
              riskScore: vpcNode?.data?.riskScore, sgCount: Object.keys(c.sgs).length, assetCount: countIn(c), raw: vpcNode?.data },
    });
    placed.forEach(b => {
      const sgNode = byId[b.id];
      if (b.kind === 'sg') parentOf[b.id] = v;
      out.push({
        id: b.id, type: 'groupBox', parentId: v, extent: 'parent', draggable: false,
        position: { x: PAD + b.x, y: HEAD + b.y }, width: b.bw, height: b.bh, style: { width: b.bw, height: b.bh },
        data: { kind: b.kind, label: sgNode?.data?.label, riskScore: sgNode?.data?.riskScore, assetCount: b.list.length, raw: sgNode?.data },
      });
      podsInto(b.id, b.list, 4);
    });
    px += vw + 48; shelfH = Math.max(shelfH, vh); colW = Math.max(colW, px - 48);
  });

  // Columns 2 and 3: identities, then everything else (buckets, repos, services outside a VPC).
  let x = colW + COL_GAP;
  [[BAND_IDENTITY, 'identity', identity], [BAND_SERVICES, 'services', services]].forEach(([id, kind, list]) => {
    if (!list.length) return;
    const g = gridSize(list.length, Math.min(5, Math.ceil(list.length / 8)));
    out.push({
      id, type: 'groupBox', position: { x, y: 0 }, draggable: false,
      width: g.w + 2 * PAD, height: HEAD + g.h + PAD,
      style: { width: g.w + 2 * PAD, height: HEAD + g.h + PAD }, data: { kind, assetCount: list.length },
    });
    podsInto(id, list, g.cols);
    x += g.w + 2 * PAD + COL_GAP;
  });

  // DEPLOYED_ON, PROTECTED_BY and BELONGS_TO are drawn as boxes (asset inside its security group,
  // inside its VPC); only the ones the boxes cannot show stay as lines.
  const shown = e => {
    const t = e.data?.edgeType, s = String(e.source), d = String(e.target);
    if (t === 'PROTECTED_BY') return parentOf[s] !== d;
    if (t === 'DEPLOYED_ON') return !(parentOf[s] === d || parentOf[parentOf[s]] === d || parentOf[s] === `${d}__loose`);
    if (t === 'BELONGS_TO') return parentOf[s] !== d;
    return true;
  };
  return { nodes: out, parentOf, visibleEdges: rawEdges.filter(shown), byId };
}

/* ───────────────────────────── visual building blocks ───────────────────────────── */

function RiskRing({ size, risk, color, children, pulse, thick }) {
  const sw = thick ? 8 : 4, r = size / 2 - sw / 2 - 1, c = 2 * Math.PI * r, rc = riskColor(risk);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      {pulse && <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', background: rc, animation: 'ag-pulse 2.2s ease-out infinite' }} />}
      <svg width={size} height={size} style={{ position: 'absolute', inset: 0 }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f7" strokeWidth={sw} />
        {risk > 0 && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={rc} strokeWidth={sw} strokeLinecap="round"
            strokeDasharray={`${(c * Math.min(risk, 100)) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        )}
      </svg>
      <div className="ag-disc" style={{
        position: 'absolute', inset: sw + 2, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#ffffff', border: `1.5px solid ${color}55`,
        boxShadow: '0 2px 8px rgba(15,23,42,.08)', transition: 'box-shadow .15s',
      }}>{children}</div>
    </div>
  );
}

function AssetPod({ data }) {
  const far = useStore(farSelector);
  const color = LAYER_COLOR[data.layer] || '#64748b';
  const risk = data.riskScore || 0;
  const ring = data._onPath ? '#dc2626' : data._selected ? '#2563eb' : null;
  if (far) {
    return (
      <div className="ag-pod" title={data.label} style={{ width: CELL_W, height: CELL_H, display: 'flex', justifyContent: 'center', opacity: data._dim ? 0.18 : 1 }}>
        <Handle type="target" position={Position.Left} style={{ top: 32, left: 34, opacity: 0 }} />
        <div style={{ borderRadius: '50%', height: 84, boxShadow: ring ? `0 0 0 5px ${ring}, 0 0 28px ${ring}aa` : 'none', marginTop: 2 }}>
          <RiskRing size={84} risk={risk} color={color} pulse={risk >= 80 && !data._dim} thick>
            <i className={`fas ${ASSET_ICON[data.assetType] || 'fa-cube'}`} style={{ color, fontSize: 26 }} />
          </RiskRing>
        </div>
        <Handle type="source" position={Position.Right} style={{ top: 32, right: 34, opacity: 0 }} />
      </div>
    );
  }
  return (
    <div className="ag-pod" style={{ width: CELL_W, height: CELL_H, display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: data._dim ? 0.18 : 1 }}>
      <Handle type="target" position={Position.Left} style={{ top: 32, left: 34, opacity: 0 }} />
      <div style={{ borderRadius: '50%', boxShadow: ring ? `0 0 0 3px ${ring}, 0 0 18px ${ring}88` : 'none', marginTop: 4 }}>
        <RiskRing size={POD + 8} risk={risk} color={color} pulse={risk >= 80 && !data._dim}>
          <i className={`fas ${ASSET_ICON[data.assetType] || 'fa-cube'}`} style={{ color, fontSize: 17 }} />
        </RiskRing>
      </div>
      <div title={data.label} style={{ marginTop: 6, maxWidth: CELL_W - 8, fontSize: 11.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {data.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#64748b' }}>
        <span>{prettyType(data.assetType)}</span>
        {risk > 0 && <span style={{ fontWeight: 700, color: riskColor(risk) }}>· {risk}</span>}
      </div>
      <Handle type="source" position={Position.Right} style={{ top: 32, right: 34, opacity: 0 }} />
    </div>
  );
}

const GROUP_STYLE = {
  vpc:      { accent: '#10b981', tint: '#f0fdf8', icon: 'fa-network-wired' },
  novpc:    { accent: '#64748b', tint: '#f8fafc', icon: 'fa-cloud', dashed: true },
  sg:       { accent: '#94a3b8', tint: '#ffffff', icon: 'fa-shield-alt' },
  loose:    { accent: '#94a3b8', tint: '#ffffff', icon: 'fa-th', dashed: true },
  identity: { accent: '#8b5cf6', tint: '#faf7ff', icon: 'fa-id-badge' },
  services: { accent: '#f59e0b', tint: '#fffbf0', icon: 'fa-cubes' },
};

function GroupBox({ data }) {
  const { t } = useTranslation();
  const far = useStore(farSelector);
  const s = GROUP_STYLE[data.kind] || GROUP_STYLE.sg;
  const hot = data.kind === 'sg' && data.riskScore >= 50;
  const accent = hot ? riskColor(data.riskScore) : s.accent;
  const outer = ['vpc', 'novpc', 'identity', 'services'].includes(data.kind);
  const title = {
    novpc: t('AttackGraphTab.groupNoVpc', 'Outside any VPC'),
    loose: t('AttackGraphTab.groupNoSg', 'Not in a security group'),
    identity: t('AttackGraphTab.groupIdentity', 'Identities'),
    services: t('AttackGraphTab.groupServices', 'Services outside a VPC'),
  }[data.kind] || data.label;
  return (
    <div style={{
      width: '100%', height: '100%', boxSizing: 'border-box', borderRadius: outer ? 20 : 14, position: 'relative',
      border: `${outer ? 1.5 : 1}px ${s.dashed ? 'dashed' : 'solid'} ${accent}${hot ? '' : '66'}`,
      background: hot ? 'rgba(254,242,242,.85)' : s.tint,
      boxShadow: data._onPath ? '0 0 0 4px rgba(220,38,38,.45), 0 0 28px rgba(220,38,38,.35)'
        : data._selected ? `0 0 0 3px ${accent}40, 0 10px 30px rgba(15,23,42,.10)` : outer ? '0 10px 30px rgba(15,23,42,.06)' : '0 2px 10px rgba(15,23,42,.05)',
      opacity: data._dim ? 0.5 : 1,
    }}>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: outer || far ? '11px 14px' : '10px 14px' }}>
        <div style={{
          width: far ? 30 : outer ? 28 : 24, height: far ? 30 : outer ? 28 : 24, flexShrink: 0, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: accent,
        }}>
          <i className={`fas ${s.icon}`} style={{ color: '#fff', fontSize: far ? 14 : outer ? 12 : 11 }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div title={title} style={{ fontWeight: 700, fontSize: far ? (outer ? 20 : 17) : outer ? 14 : 12.5, color: hot ? accent : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: far ? 'none' : 320 }}>{title}</div>
          {!far && (
            <div style={{ fontSize: 10.5, color: '#64748b', letterSpacing: '.02em' }}>
              {data.kind === 'vpc' ? `${data.sgCount} security groups · ${data.assetCount} assets` : `${data.assetCount} asset${data.assetCount === 1 ? '' : 's'}`}
              {hot ? ' · exposed' : ''}
            </div>
          )}
        </div>
        {data.riskScore > 0 && (
          <div style={{ marginLeft: 'auto', fontSize: far ? 17 : 11.5, fontWeight: 800, color: '#fff', background: riskColor(data.riskScore), borderRadius: 999, padding: far ? '2px 12px' : '2px 9px', boxShadow: `0 3px 10px ${riskColor(data.riskScore)}66` }}>
            {data.riskScore}
          </div>
        )}
      </div>
      {data.assetCount === 0 && !far && <div style={{ padding: '0 16px', fontSize: 11, color: '#94a3b8' }}>{t('AttackGraphTab.groupEmpty', 'No assets attached')}</div>}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  );
}

const nodeTypes = { assetPod: AssetPod, groupBox: GroupBox };

/* ───────────────────────────── insight strip ───────────────────────────── */

function Donut({ parts, size = 64 }) {
  const total = parts.reduce((n, p) => n + p.value, 0) || 1;
  const r = size / 2 - 6, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f7" strokeWidth="9" />
      {parts.map(p => {
        const len = (c * p.value) / total;
        const el = <circle key={p.key} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={p.color} strokeWidth="9"
          strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} transform={`rotate(-90 ${size / 2} ${size / 2})`} />;
        offset += len;
        return el;
      })}
    </svg>
  );
}

function InsightCard({ children }) {
  return (
    <div style={{
      flex: 1, minWidth: 220, borderRadius: 16, padding: '14px 16px', position: 'relative', overflow: 'hidden',
      background: '#ffffff', border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    }}>
      {children}
    </div>
  );
}

const kicker = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#64748b' };
const Kicker = ({ color, children }) => (
  <div style={kicker}><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 2, background: color, marginRight: 7, verticalAlign: 1 }} />{children}</div>
);

function InsightStrip({ raw, onSelect }) {
  const { t } = useTranslation();
  const assets = raw.nodes;
  const bands = RISK_BANDS.map(b => ({ ...b, value: 0 }));
  assets.forEach(n => { const r = n.data?.riskScore || 0; bands.find(b => r >= b.min).value += 1; });
  const layers = {};
  assets.forEach(n => { const l = n.data?.layer || 'OTHER'; layers[l] = (layers[l] || 0) + 1; });
  const exposedSgs = assets.filter(n => n.data?.assetType === 'SECURITY_GROUP' && (n.data?.riskScore || 0) >= 50).sort(byRisk);
  const blast = [...assets].sort((a, b) => (b.data?.blastRadius || 0) - (a.data?.blastRadius || 0))[0];

  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
      <InsightCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Donut parts={bands} />
          <div>
            <Kicker color="#dc2626">{t('AttackGraphTab.riskMix', 'Risk mix')}</Kicker>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 12, rowGap: 2, marginTop: 4, fontSize: 12 }}>
              {bands.map(b => (
                <React.Fragment key={b.key}>
                  <span style={{ color: '#475569' }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: b.color, marginRight: 6 }} />{b.key}</span>
                  <b style={{ color: '#0f172a' }}>{b.value}</b>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </InsightCard>
      <InsightCard>
        <Kicker color="#2563eb">{t('AttackGraphTab.estate', 'Estate')}</Kicker>
        <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', margin: '2px 0 8px' }}>{assets.length} <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>{t('AttackGraphTab.assetsLabel')} · {raw.edges.length} {t('AttackGraphTab.edgesLabel')}</span></div>
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eef2f7' }}>
          {Object.entries(layers).map(([l, n]) => <div key={l} title={`${l}: ${n}`} style={{ flex: n, background: LAYER_COLOR[l] || '#94a3b8' }} />)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', fontSize: 11, color: '#64748b' }}>
          {Object.entries(layers).map(([l, n]) => <span key={l}><b style={{ color: LAYER_COLOR[l] || '#64748b' }}>{n}</b> {l.toLowerCase()}</span>)}
        </div>
      </InsightCard>
      <InsightCard>
        <Kicker color="#ea580c">{t('AttackGraphTab.exposedSgs', 'Risky security groups')}</Kicker>
        <div style={{ fontSize: 26, fontWeight: 800, color: exposedSgs.length ? '#ea580c' : '#0f172a', margin: '2px 0 6px' }}>{exposedSgs.length}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {exposedSgs.slice(0, 3).map(n => (
            <button key={n.id} className="ag-chip" onClick={() => onSelect(String(n.id))} style={{ border: '1px solid #fed7aa', background: '#fff7ed', color: '#9a3412', borderRadius: 999, padding: '2px 9px', fontSize: 11, cursor: 'pointer' }}>
              {n.data.label} · {n.data.riskScore}
            </button>
          ))}
          {exposedSgs.length === 0 && <span style={{ fontSize: 12, color: '#64748b' }}>{t('AttackGraphTab.noneRisky', 'None above 50')}</span>}
        </div>
      </InsightCard>
      <InsightCard>
        <Kicker color="#7c3aed">{t('AttackGraphTab.biggestBlast', 'Biggest blast radius')}</Kicker>
        {blast && blast.data?.blastRadius > 0 ? (
          <div onClick={() => onSelect(String(blast.id), 'radar')} style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#7c3aed', margin: '2px 0 2px' }}>{blast.data.blastRadius} <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>{t('AttackGraphTab.reachable', 'assets reachable')}</span></div>
            <div style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 600 }}><i className={`fas ${ASSET_ICON[blast.data.assetType] || 'fa-cube'}`} style={{ color: LAYER_COLOR[blast.data.layer], marginRight: 6 }} />{blast.data.label}
              <span style={{ color: '#2563eb', fontWeight: 600, marginLeft: 8 }}>{t('AttackGraphTab.openRadar', 'Open radar →')}</span></div>
          </div>
        ) : <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6 }}>—</div>}
      </InsightCard>
    </div>
  );
}

/* ───────────────────────────── blast radius radar ───────────────────────────── */

// Same depth as the backend's blast radius (findReachableAssets, 4 hops), so the counts agree.
const RADAR_HOPS = 4;

function hopsFrom(edges, start, dir, max = RADAR_HOPS) {
  const adj = {};
  edges.forEach(e => {
    const s = String(e.source), d = String(e.target);
    const [a, b] = dir === 'out' ? [s, d] : [d, s];
    (adj[a] = adj[a] || []).push({ to: b, type: e.data?.edgeType });
  });
  const hop = { [start]: 0 }, parent = {}, q = [start];
  while (q.length) {
    const u = q.shift();
    if (hop[u] >= max) continue;
    (adj[u] || []).forEach(({ to, type }) => { if (hop[to] === undefined) { hop[to] = hop[u] + 1; parent[to] = { from: u, type }; q.push(to); } });
  }
  delete hop[start];
  return { hop, parent };
}

const shortLabel = (s, n = 22) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '');

function BlastRadar({ centerId, byId, edges, onSelect }) {
  const { t } = useTranslation();
  const W = 1200, H = 760, cx = W / 2, cy = H / 2 + 8, R = [0, 160, 230, 290, 345];
  const RO = R[RADAR_HOPS];
  const out = useMemo(() => hopsFrom(edges, centerId, 'out'), [edges, centerId]);
  const inc = useMemo(() => hopsFrom(edges, centerId, 'in'), [edges, centerId]);
  if (!byId[centerId]) return null;

  const outCount = Object.keys(out.hop).length, inCount = Object.keys(inc.hop).length;
  const rings = { out: {}, in: {} };
  Object.entries(out.hop).forEach(([id, h]) => { (rings.out[h] = rings.out[h] || []).push(id); });
  Object.entries(inc.hop).forEach(([id, h]) => { if (out.hop[id] === undefined) (rings.in[h] = rings.in[h] || []).push(id); });

  // "Reaches" is the right half and "reached by" the left half. When the other side is empty and a
  // ring would be crowded in its half, the assets get nearly the whole circle instead (the gap left
  // over holds the hop labels).
  const halfRad = (160 * Math.PI) / 180;
  const crowdedInHalf = s => Object.entries(rings[s]).some(([h, ids]) => ids.length > Math.floor((halfRad * R[h]) / 120));
  const wide = (outCount > 0) !== (inCount > 0) && crowdedInHalf(outCount ? 'out' : 'in');
  const spanOf = s => (!wide ? (s === 'out' ? [-80, 80] : [100, 260]) : s === 'out' ? [-160, 160] : [20, 340]);
  const spanRad = s => ((spanOf(s)[1] - spanOf(s)[0]) * Math.PI) / 180;

  // A crowded ring may move a little outward (never past the next ring) so its pods fit in one row.
  const ringR = R.map((r, h) => {
    if (!h) return 0;
    let v = r;
    ['out', 'in'].forEach(s => {
      const n = (rings[s][h] || []).length;
      if (n > Math.floor((spanRad(s) * r) / 120)) v = Math.max(v, Math.min((n * 46) / spanRad(s), (R[h + 1] ?? r + 60) - 30));
    });
    return v;
  });

  // Rings with few assets show names under each pod. A crowded ring uses small pods grouped by type
  // with names running outward; if one row cannot hold them, pods alternate between two rows and the
  // lowest-risk overflow is summarised as "+N more".
  const pos = { [centerId]: { x: cx, y: cy } }, compact = {}, more = [];
  ['out', 'in'].forEach(s => {
    const [a0, a1] = spanOf(s);
    Object.entries(rings[s]).forEach(([hs, ids]) => {
      const h = Number(hs), r0 = ringR[h], arc = spanRad(s) * r0;
      const roomy = ids.length <= Math.floor(arc / 120);
      const oneRow = ids.length <= Math.floor(arc / 46);
      const keep = [...ids].sort((p, q) => byRisk(byId[p], byId[q])).slice(0, oneRow ? ids.length : Math.floor(arc / 46) * 2);
      if (!roomy) keep.sort((p, q) => String(byId[p]?.data?.assetType).localeCompare(String(byId[q]?.data?.assetType)) || byRisk(byId[p], byId[q]));
      keep.forEach((id, i) => {
        const a = ((a0 + ((a1 - a0) * (i + 0.5)) / keep.length) * Math.PI) / 180;
        const r = r0 + (roomy || oneRow ? 0 : i % 2 ? 16 : -16);
        pos[id] = { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a), a, labelR: r0 + (oneRow ? 28 : 40) };
        if (!roomy) compact[id] = true;
      });
      if (ids.length > keep.length) more.push({ h, side: s === 'out' ? 1 : -1, count: ids.length - keep.length });
    });
  });
  const links = [
    ...Object.entries(out.parent).map(([v, p]) => ({ a: p.from, b: v, type: p.type, dir: 'out' })),
    ...Object.entries(inc.parent).map(([v, p]) => ({ a: v, b: p.from, type: p.type, dir: 'in' })),
  ].filter(l => pos[l.a] && pos[l.b]);
  const podR = id => (id === centerId ? 46 : compact[id] ? 22 : 32);
  const hopLabel = (r, i) => {
    const text = `${i + 1} HOP${i ? 'S' : ''}`;
    if (!wide) return <text key={r} x={cx + 6} y={cy - r - 6} fontSize="10.5" fill="#94a3b8" fontWeight="700" letterSpacing="1">{text}</text>;
    const right = inCount > 0;
    return (
      <text key={r} x={right ? cx + r + 6 : cx - r - 6} y={cy + 4} textAnchor={right ? 'start' : 'end'} fontSize="10.5" fill="#94a3b8" fontWeight="700" letterSpacing="1">{text}</text>
    );
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <marker id="ag-arrow-out" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#2563eb" /></marker>
        <marker id="ag-arrow-in" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#dc2626" /></marker>
        <marker id="ag-arrow-out-mid" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#2563eb" /></marker>
        <marker id="ag-arrow-in-mid" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#dc2626" /></marker>
        <filter id="ag-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      {wide ? (
        <circle cx={cx} cy={cy} r={RO + 22} fill={inCount ? '#fef2f2' : '#eff6ff'} fillOpacity=".7" />
      ) : (
        <>
          <path d={`M${cx},${cy - RO - 22} A${RO + 22},${RO + 22} 0 0,0 ${cx},${cy + RO + 22} Z`} fill="#fef2f2" fillOpacity=".7" stroke="none" />
          <path d={`M${cx},${cy - RO - 22} A${RO + 22},${RO + 22} 0 0,1 ${cx},${cy + RO + 22} Z`} fill="#eff6ff" fillOpacity=".7" stroke="none" />
          <line x1={cx} y1={cy - RO - 22} x2={cx} y2={cy + RO + 22} stroke="#e2e8f0" />
        </>
      )}
      {ringR.slice(1).map((r, i) => (
        <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#cbd5e1" strokeDasharray={i === RADAR_HOPS - 1 ? '' : '3 7'} />
      ))}
      {ringR.slice(1).map(hopLabel)}
      <text x={cx - RO - 40} y={cy - RO + 10} fontSize="12" fontWeight="800" fill="#b91c1c" letterSpacing="1.4" textAnchor="end">← {t('AttackGraphTab.reachedBy', 'REACHED BY')} ({inCount})</text>
      {inCount === 0 && <text x={cx - RO - 40} y={cy - RO + 28} fontSize="11.5" fill="#94a3b8" textAnchor="end">{t('AttackGraphTab.radarNothingIn', 'Nothing reaches this asset')}</text>}
      <text x={cx + RO + 40} y={cy - RO + 10} fontSize="12" fontWeight="800" fill="#1d4ed8" letterSpacing="1.4">{t('AttackGraphTab.reaches', 'REACHES')} ({outCount}) →</text>
      {outCount === 0 && <text x={cx + RO + 40} y={cy - RO + 28} fontSize="11.5" fill="#94a3b8">{t('AttackGraphTab.radarNothingOut', 'It reaches nothing else')}</text>}
      {links.map((l, i) => {
        const A = pos[l.a], B = pos[l.b], dx = B.x - A.x, dy = B.y - A.y, L = Math.hypot(dx, dy) || 1;
        const color = l.dir === 'out' ? '#2563eb' : '#dc2626';
        const ra = podR(l.a), rb = podR(l.b) + 2;
        const x1 = A.x + (dx * ra) / L, y1 = A.y + (dy * ra) / L, x2 = B.x - (dx * rb) / L, y2 = B.y - (dy * rb) / L;
        if (compact[l.a] || compact[l.b]) {
          // Crowded ring: thin light lines with the arrow halfway, so arrowheads do not pile up at the centre.
          return (
            <polyline key={i} points={`${x1},${y1} ${(x1 + x2) / 2},${(y1 + y2) / 2} ${x2},${y2}`} fill="none"
              stroke={color} strokeWidth="1.2" strokeOpacity=".45" markerMid={`url(#ag-arrow-${l.dir}-mid)`} />
          );
        }
        const at = l.a === centerId ? 0.62 : l.b === centerId ? 0.38 : 0.52;
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="2" strokeOpacity=".8" markerEnd={`url(#ag-arrow-${l.dir})`} filter="url(#ag-glow)" />
            <text x={A.x + dx * at} y={A.y + dy * at + 3} fontSize="9.5" fontWeight="600" fill={color} textAnchor="middle"
              stroke="#fff" strokeWidth="4" paintOrder="stroke" strokeLinejoin="round">{prettyType(l.type)}</text>
          </g>
        );
      })}
      {more.map(m => (
        <text key={`${m.side}-${m.h}`} x={cx + m.side * (RO + 40)} y={cy + RO - 70 + m.h * 18} fontSize="11.5" fontWeight="600"
          fill={m.side > 0 ? '#1d4ed8' : '#b91c1c'} textAnchor={m.side > 0 ? 'start' : 'end'}>
          {t('AttackGraphTab.radarMore', '+{{count}} more at {{hops}} hop(s)', { count: m.count, hops: m.h })}
        </text>
      ))}
      {Object.entries(pos).map(([id, p]) => {
        const n = byId[id]; if (!n) return null;
        const isC = id === centerId, small = compact[id], size = isC ? 84 : small ? 40 : 56, color = LAYER_COLOR[n.data.layer] || '#64748b';
        return (
          <foreignObject key={id} x={p.x - 70} y={p.y - size / 2 - (isC ? 6 : 2)} width="140" height={size + (small ? 8 : 46)} style={{ overflow: 'visible', pointerEvents: 'none' }}>
            <div className="ag-pod" onClick={() => onSelect(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'fit-content', margin: '0 auto', pointerEvents: 'auto' }}
              title={`${n.data.label} · ${prettyType(n.data.assetType)}${n.data.riskScore ? ` · risk ${n.data.riskScore}` : ''}`}>
              <div style={{ borderRadius: '50%', boxShadow: isC ? '0 0 0 4px rgba(37,99,235,.25), 0 0 30px rgba(37,99,235,.35)' : 'none' }}>
                <RiskRing size={size} risk={n.data.riskScore || 0} color={color} pulse={(n.data.riskScore || 0) >= 80}>
                  <i className={`fas ${ASSET_ICON[n.data.assetType] || 'fa-cube'}`} style={{ color, fontSize: isC ? 24 : small ? 12 : 16 }} />
                </RiskRing>
              </div>
              {!small && (
                <>
                  <div style={{ marginTop: 4, fontSize: isC ? 13 : 11, fontWeight: 700, color: '#0f172a', maxWidth: 136, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.data.label}</div>
                  <div style={{ fontSize: 9.5, color: '#64748b' }}>{prettyType(n.data.assetType)}</div>
                </>
              )}
            </div>
          </foreignObject>
        );
      })}
      {/* Names of the small pods on a crowded ring: they run outward along each pod's spoke and all start
          just outside the pods, so neighbouring names never overlap. */}
      {Object.entries(pos).filter(([id]) => compact[id] && byId[id]).map(([id, p]) => {
        const deg = (p.a * 180) / Math.PI, right = Math.cos(p.a) >= 0;
        const lx = cx + p.labelR * Math.cos(p.a), ly = cy + p.labelR * Math.sin(p.a);
        return (
          <text key={`label-${id}`} x={lx} y={ly} transform={`rotate(${right ? deg : deg - 180} ${lx} ${ly})`} textAnchor={right ? 'start' : 'end'}
            dominantBaseline="central" fontSize="11.5" fontWeight="600" fill="#0f172a" stroke="#fff" strokeWidth="3" paintOrder="stroke" strokeLinejoin="round"
            style={{ cursor: 'pointer' }} onClick={() => onSelect(id)}>
            <title>{byId[id].data.label}</title>
            {shortLabel(byId[id].data.label)}
          </text>
        );
      })}
    </svg>
  );
}

/* ───────────────────────────── side panel ───────────────────────────── */

function SidePanel({ selected, layout, rawEdges, onSelect, onSimFrom, onSimTo, onRadar }) {
  const { t } = useTranslation();
  const { byId, parentOf } = layout;
  const assets = Object.values(byId).filter(n => n.data);

  if (!selected) {
    const topRisk = [...assets].sort(byRisk).filter(n => n.data.riskScore > 0).slice(0, 8);
    return (
      <div>
        <PanelTitle>{t('AttackGraphTab.highestRisk', 'Highest risk')}</PanelTitle>
        {topRisk.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8' }}>{t('AttackGraphTab.noRisk', 'No risk scores yet.')}</div>}
        {topRisk.map((n, i) => (
          <div key={n.id} className="ag-row" onClick={() => onSelect(String(n.id))} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 10, cursor: 'pointer' }}>
            <span style={{ width: 18, fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{i + 1}</span>
            <RiskRing size={30} risk={n.data.riskScore} color={LAYER_COLOR[n.data.layer] || '#64748b'}>
              <i className={`fas ${ASSET_ICON[n.data.assetType] || 'fa-cube'}`} style={{ color: LAYER_COLOR[n.data.layer] || '#64748b', fontSize: 9 }} />
            </RiskRing>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.data.label}</div>
              <div style={{ fontSize: 10.5, color: '#64748b' }}>{prettyType(n.data.assetType)}</div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 800, color: riskColor(n.data.riskScore) }}>{n.data.riskScore}</span>
          </div>
        ))}
        <PanelTitle style={{ marginTop: 18 }}>{t('AttackGraphTab.howToRead', 'How to read it')}</PanelTitle>
        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
          <div><RingSwatch /> {t('AttackGraphTab.legendRing', 'Ring = risk score; pulsing = 80+')}</div>
          <div><span style={{ display: 'inline-block', width: 16, height: 10, borderRadius: 3, border: '1.5px solid #10b981', marginRight: 6, verticalAlign: -1 }} />{t('AttackGraphTab.legendVpc', 'VPC, with its security groups inside')}</div>
          <div><span style={{ display: 'inline-block', width: 16, height: 10, borderRadius: 3, border: '1.5px solid #dc2626', background: '#fee2e2', marginRight: 6, verticalAlign: -1 }} />{t('AttackGraphTab.legendRiskySg', 'Security group with risk 50+')}</div>
          <div><span style={{ color: '#8b5cf6', fontWeight: 800, marginRight: 6 }}>⟶</span>{t('AttackGraphTab.legendAssumes', 'Assumes an IAM role')}</div>
          <div><span style={{ color: '#dc2626', fontWeight: 800, marginRight: 6 }}>⟶</span>{t('AttackGraphTab.legendPath', 'Simulated attack path')}</div>
        </div>
      </div>
    );
  }

  const n = byId[selected];
  if (!n) return null;
  const color = LAYER_COLOR[n.data.layer] || '#64748b';
  const conns = rawEdges.filter(e => String(e.source) === selected || String(e.target) === selected).map(e => {
    const outgoing = String(e.source) === selected;
    return { other: byId[String(outgoing ? e.target : e.source)], type: e.data?.edgeType || e.label, outgoing };
  }).filter(c => c.other);
  const groups = {};
  conns.forEach(c => { const k = `${c.outgoing ? '→' : '←'} ${prettyType(c.type)}`; (groups[k] = groups[k] || []).push(c.other); });
  const location = [parentOf[parentOf[selected]], parentOf[selected]].map(id => byId[id]?.data?.label).filter(Boolean);
  const risk = n.data.riskScore || 0;

  return (
    <div>
      <div style={{ borderRadius: 16, padding: 14, marginBottom: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderLeft: `3px solid ${color}`, position: 'relative' }}>
        <button onClick={() => onSelect(null)} title="Close" style={{ position: 'absolute', right: 8, top: 8, padding: 4, margin: 0, lineHeight: 1, border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>
          <i className="fas fa-times" />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RiskRing size={64} risk={risk} color={color} pulse={risk >= 80}>
            <i className={`fas ${ASSET_ICON[n.data.assetType] || 'fa-cube'}`} style={{ color, fontSize: 19 }} />
          </RiskRing>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', wordBreak: 'break-all' }}>{n.data.label}</div>
            <div style={{ fontSize: 11.5, color: '#64748b' }}>{prettyType(n.data.assetType)}{n.data.cloudProvider ? ` · ${n.data.cloudProvider}` : ''}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <Metric value={risk || '—'} label={t('AttackGraphTab.metricRisk', 'Risk score')} color={riskColor(risk)} />
          <Metric value={n.data.blastRadius ?? '—'} label={t('AttackGraphTab.metricBlast', 'Blast radius')} color="#7c3aed" />
        </div>
      </div>
      {location.length > 0 && (
        <>
          <PanelTitle>{t('AttackGraphTab.location', 'Location')}</PanelTitle>
          <div style={{ fontSize: 12.5, color: '#334155', marginBottom: 14 }}><i className="fas fa-map-marker-alt" style={{ color: '#94a3b8', marginRight: 6 }} />{location.join('  ›  ')}</div>
        </>
      )}
      <PanelTitle>{t('AttackGraphTab.connections', 'Connections')} ({conns.length})</PanelTitle>
      {conns.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>{t('AttackGraphTab.noConnections', 'No relations recorded for this asset.')}</div>}
      {Object.entries(groups).map(([k, list]) => (
        <div key={k} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>{k}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {list.map(o => (
              <button key={o.id} className="ag-chip" onClick={() => onSelect(String(o.id))} style={{
                display: 'flex', alignItems: 'center', gap: 5, border: `1px solid ${(LAYER_COLOR[o.data.layer] || '#94a3b8')}44`, background: '#fff', borderRadius: 999,
                padding: '3px 10px', fontSize: 11.5, color: '#334155', cursor: 'pointer', maxWidth: 270, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <i className={`fas ${ASSET_ICON[o.data.assetType] || 'fa-cube'}`} style={{ color: LAYER_COLOR[o.data.layer] || '#94a3b8', fontSize: 10 }} />{o.data.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
        <button onClick={() => onRadar(selected)} style={{ ...actionBtn, background: '#4f46e5', color: '#fff', border: 'none' }}>
          <i className="fas fa-bullseye" style={{ marginRight: 6 }} />{t('AttackGraphTab.showBlast', 'Show blast radius')}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onSimFrom(selected)} style={{ ...actionBtn, flex: 1 }}><i className="fas fa-crosshairs" style={{ marginRight: 5, color: '#ef4444' }} />{t('AttackGraphTab.useAsSource', 'Simulate from')}</button>
          <button onClick={() => onSimTo(selected)} style={{ ...actionBtn, flex: 1 }}><i className="fas fa-flag-checkered" style={{ marginRight: 5, color: '#2563eb' }} />{t('AttackGraphTab.useAsTarget', 'Use as target')}</button>
        </div>
      </div>
    </div>
  );
}

const actionBtn = { padding: '8px 10px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#334155', cursor: 'pointer' };

function RingSwatch() {
  return <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '3px solid #ea580c', borderRightColor: '#eef2f7', marginRight: 6, verticalAlign: -2 }} />;
}

function PanelTitle({ children, style }) {
  return <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: '#64748b', margin: '0 0 8px', ...style }}>{children}</div>;
}

function Metric({ value, label, color }) {
  return (
    <div style={{ background: '#ffffffcc', border: '1px solid #e8edf5', borderRadius: 12, padding: '8px 12px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748b' }}>{label}</div>
    </div>
  );
}

function SimResult({ result, nodeById, onClear }) {
  const { t } = useTranslation();
  if (!result.canReach) {
    return (
      <div style={{ marginTop: '12px', padding: '12px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px' }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: '#065f46' }}>
          <i className="fas fa-check-circle" style={{ marginRight: '8px' }} />
          {t('AttackGraphTab.noPathFound')}
        </div>
      </div>
    );
  }
  const paths = result.paths || [];
  return (
    <div style={{ marginTop: '12px', padding: '14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: '14px', color: '#991b1b', marginBottom: paths.length ? '12px' : 0 }}>
        <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }} />
        {t(paths.length !== 1 ? 'AttackGraphTab.attackPathExists_other' : 'AttackGraphTab.attackPathExists_one', { count: paths.length, assetCount: result.reachableAssetCount })}
        <button onClick={onClear} style={{ marginLeft: 'auto', border: '1px solid #fca5a5', background: '#fff', color: '#991b1b', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
          {t('AttackGraphTab.clearPath', 'Clear highlight')}
        </button>
      </div>
      {paths.map((path, pi) => (
        <div key={pi} style={{ marginTop: pi > 0 ? '10px' : 0 }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#7f1d1d', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('AttackGraphTab.pathLabel', { number: pi + 1 })}{pi === 0 ? ` · ${t('AttackGraphTab.highlightedOnGraph', 'highlighted on the graph')}` : ''}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            {path.map((nodeId, idx) => {
              const node = nodeById[String(nodeId)];
              const color = LAYER_COLOR[node?.data?.layer] || '#64748b';
              return (
                <React.Fragment key={`${nodeId}-${idx}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: `1px solid ${color}55`, borderRadius: '999px', padding: '4px 12px 4px 6px', fontSize: '12px', boxShadow: '0 2px 6px rgba(15,23,42,.06)' }}>
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: color + '1f', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={`fas ${ASSET_ICON[node?.data?.assetType] || 'fa-cube'}`} style={{ color, fontSize: '10px' }} />
                    </span>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{node?.data?.label || nodeId}</span>
                  </div>
                  {idx < path.length - 1 && <i className="fas fa-long-arrow-alt-right" style={{ color: '#dc2626', fontSize: '14px', flexShrink: 0 }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Chip({ active, color, onClick, children }) {
  return (
    <button className="ag-chip" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 999, cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
      border: `1px solid ${active ? (color || '#2563eb') + '66' : '#e2e8f0'}`, background: active ? (color || '#2563eb') + '14' : '#fff',
      color: active ? '#0f172a' : '#94a3b8', transition: 'all .15s',
    }}>{children}</button>
  );
}

// Padding, margin and line height are set here because the app has global button rules
// (e.g. padding 10px 20px) that would otherwise push the icon off-centre.
function ToolButton({ icon, title, onClick, active, disabled }) {
  return (
    <button className="ag-tool" onClick={onClick} title={title} aria-label={title} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box',
      width: 32, height: 32, padding: 0, margin: 0, lineHeight: 1, border: 'none', borderRadius: 8, fontSize: 13,
      cursor: disabled ? 'default' : 'pointer',
      background: active ? '#eff6ff' : 'transparent', color: disabled ? '#cbd5e1' : active ? '#1d4ed8' : '#334155',
    }}><i className={`fas ${icon}`} style={{ lineHeight: 1 }} /></button>
  );
}

// Own component so only this label re-renders while the user zooms or pans.
function ZoomLevel() {
  const zoom = useStore(s => s.transform[2]);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, height: 32, lineHeight: 1,
      fontSize: 11.5, fontWeight: 700, color: '#475569', fontVariantNumeric: 'tabular-nums',
    }}>{Math.round(zoom * 100)}%</span>
  );
}

/* ───────────────────────────── main ───────────────────────────── */

function AttackGraph({ orgId }) {
  const { t } = useTranslation();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const far = useStore(farSelector);
  const workspaceRef = useRef(null);
  const [isFull, setIsFull] = useState(false);
  const [showMinimap, setShowMinimap] = useState(true);
  const [raw, setRaw] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(false);
  const [simFrom, setSimFrom] = useState('');
  const [simTo, setSimTo] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('map');
  const [radarCenter, setRadarCenter] = useState(null);
  const [query, setQuery] = useState('');
  const [hiddenLayers, setHiddenLayers] = useState(new Set());
  const [riskyOnly, setRiskyOnly] = useState(false);

  const loadGraph = useCallback(() => {
    setLoading(true);
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/graph', { params })
      .then(r => setRaw({ nodes: r.data.nodes || [], edges: r.data.edges || [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  // Full screen covers the graph and its side panel; Esc (handled by the browser) also leaves it.
  useEffect(() => {
    const onChange = () => {
      setIsFull(document.fullscreenElement === workspaceRef.current);
      setTimeout(() => fitView({ duration: 300, padding: 0.12 }), 150);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [fitView]);
  const toggleFull = () => {
    if (document.fullscreenElement) { setIsFull(false); document.exitFullscreen(); }
    else if (workspaceRef.current?.requestFullscreen) workspaceRef.current.requestFullscreen();
  };

  const layout = useMemo(() => buildLayout(raw.nodes, raw.edges), [raw]);

  // Only assets that take part in edges can be simulated (same rule as before).
  const nodeList = useMemo(() => {
    const sourceIds = new Set(raw.edges.map(e => String(e.source)));
    const connectedIds = new Set([...sourceIds, ...raw.edges.map(e => String(e.target))]);
    return {
      sources: raw.nodes.filter(n => sourceIds.has(String(n.id))),
      connected: raw.nodes.filter(n => connectedIds.has(String(n.id))),
    };
  }, [raw]);

  const pathIds = useMemo(() => (simResult?.canReach && simResult.paths?.[0] ? simResult.paths[0].map(String) : []), [simResult]);

  const neighbourhood = useMemo(() => {
    if (!selected) return null;
    const set = new Set([selected]);
    raw.edges.forEach(e => {
      const s = String(e.source), d = String(e.target);
      if (s === selected) set.add(d);
      if (d === selected) set.add(s);
    });
    return set;
  }, [selected, raw.edges]);

  const q = query.trim().toLowerCase();
  const layers = useMemo(() => [...new Set(raw.nodes.map(n => n.data?.layer).filter(Boolean))], [raw.nodes]);

  const nodes = useMemo(() => {
    const onPath = new Set(pathIds);
    return layout.nodes.map(n => {
      const d = n.data || {};
      const isPod = n.type === 'assetPod';
      const dim = isPod && (
        hiddenLayers.has(d.layer)
        || (riskyOnly && (d.riskScore || 0) < 50)
        || (q && !String(d.label || '').toLowerCase().includes(q))
        || (neighbourhood && !neighbourhood.has(n.id))
        || (onPath.size > 0 && !onPath.has(n.id))
      );
      return { ...n, data: { ...d, _dim: dim, _selected: n.id === selected, _onPath: onPath.has(n.id) } };
    });
  }, [layout.nodes, hiddenLayers, riskyOnly, q, neighbourhood, pathIds, selected]);

  const edges = useMemo(() => {
    const base = layout.visibleEdges.map(e => {
      const type = e.data?.edgeType;
      const touches = selected && (String(e.source) === selected || String(e.target) === selected);
      const color = touches ? '#2563eb' : (EDGE_COLOR[type] || '#94a3b8');
      const faded = (selected && !touches) || pathIds.length > 0;
      return {
        id: String(e.id), source: String(e.source), target: String(e.target),
        className: type === 'ASSUMES_ROLE' && !faded ? 'ag-flow' : undefined,
        animated: type === 'ASSUMES_ROLE' && !faded,
        label: touches ? prettyType(type) : undefined,
        labelStyle: { fontSize: far ? 20 : 10, fill: '#1d4ed8', fontWeight: 600 }, labelBgStyle: { fill: '#eff6ff' }, labelBgPadding: far ? [10, 5] : [6, 3], labelBgBorderRadius: 6,
        style: { stroke: color, strokeWidth: (touches ? 2.6 : 1.6) * (far ? 2 : 1), opacity: faded ? 0.12 : 0.9, strokeDasharray: type === 'ASSUMES_ROLE' || touches ? undefined : '4 5' },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
      };
    });
    const sim = pathIds.slice(0, -1).map((id, i) => ({
      id: `sim-${i}`, source: id, target: pathIds[i + 1], animated: true, zIndex: 1000, className: 'ag-hot',
      style: { stroke: '#dc2626', strokeWidth: far ? 6 : 3.2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#dc2626', width: 16, height: 16 },
    }));
    return [...base, ...sim];
  }, [layout.visibleEdges, selected, pathIds, far]);

  // Select an asset and zoom to it and the assets it is linked to (boxes it sits in are left out of the fit).
  const focus = useCallback((id, target) => {
    setSelected(id);
    if (!id) return;
    if (target === 'radar') { setRadarCenter(id); setView('radar'); return; }
    if (view === 'radar') { setRadarCenter(id); return; }
    const pods = new Set(layout.nodes.filter(n => n.type === 'assetPod').map(n => n.id));
    const ids = new Set([id]);
    raw.edges.forEach(e => {
      const s = String(e.source), d = String(e.target);
      if (s === id && pods.has(d)) ids.add(d);
      if (d === id && pods.has(s)) ids.add(s);
    });
    setTimeout(() => fitView({ nodes: [...ids].map(x => ({ id: x })), duration: 600, maxZoom: 1.1, padding: ids.size > 1 ? 0.3 : 0.8 }), 0);
  }, [fitView, view, layout.nodes, raw.edges]);

  const onSearchKey = e => {
    if (e.key !== 'Enter' || !q) return;
    const hit = layout.nodes.find(n => n.type === 'assetPod' && String(n.data?.label || '').toLowerCase().includes(q));
    if (hit) focus(hit.id);
  };

  const simulate = () => {
    if (!simFrom || !simTo) return;
    setSimLoading(true);
    const simParams = orgId ? { orgId } : {};
    axios.post('/iasm/attack-path/simulate', { fromAssetId: Number(simFrom), toAssetId: Number(simTo) }, { params: simParams })
      .then(r => { setSimResult(r.data); setSelected(null); setView('map'); setTimeout(() => fitView({ duration: 600 }), 0); })
      .catch(() => {})
      .finally(() => setSimLoading(false));
  };

  const defaultCenter = useMemo(() => {
    const top = [...raw.nodes].sort((a, b) => (b.data?.blastRadius || 0) - (a.data?.blastRadius || 0) || byRisk(a, b))[0];
    return top ? String(top.id) : null;
  }, [raw.nodes]);
  const center = radarCenter || selected || defaultCenter;

  const isMap = view === 'map';
  const bar = {
    display: 'flex', alignItems: 'center', gap: 6, padding: 6, borderRadius: 12, pointerEvents: 'auto',
    background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(15,23,42,.08)',
  };

  return (
    <div>
      <style>{GRAPH_CSS}</style>
      <InsightStrip raw={raw} onSelect={focus} />

      <div ref={workspaceRef} style={{ display: 'flex', gap: 16, background: isFull ? '#f1f5f9' : 'transparent', padding: isFull ? 12 : 0 }}>
        <div className="ag-canvas" style={{
          flex: 1, minWidth: 0, height: isFull ? '100%' : '720px', border: '1px solid #e2e8f0', borderRadius: isFull ? 12 : 20,
          overflow: 'hidden', position: 'relative', background: '#ffffff',
          // The topology view draws its own dots; the radar gets the same dotted workspace from CSS.
          ...(isMap ? {} : { backgroundImage: 'radial-gradient(#cbd5e1 1.3px, transparent 1.3px)', backgroundSize: '22px 22px' }),
        }}>
          <div style={{ position: 'absolute', zIndex: 5, left: 12, right: 12, top: 12, display: 'flex', alignItems: 'flex-start', gap: 10, pointerEvents: 'none' }}>
            {/* left: view, search and filters */}
            <div style={{ ...bar, flexWrap: 'wrap', minWidth: 0 }}>
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 9, padding: 3 }}>
                {[['map', 'fa-project-diagram', t('AttackGraphTab.viewMap', 'Topology')], ['radar', 'fa-bullseye', t('AttackGraphTab.viewRadar', 'Blast radius')]].map(([v, icon, label]) => (
                  <button key={v} onClick={() => setView(v)} style={{
                    border: 'none', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    background: view === v ? '#fff' : 'transparent', color: view === v ? '#1d4ed8' : '#64748b', boxShadow: view === v ? '0 1px 3px rgba(15,23,42,.12)' : 'none',
                  }}><i className={`fas ${icon}`} style={{ marginRight: 6 }} />{label}</button>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: 9, color: '#94a3b8', fontSize: 12 }} />
                <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onSearchKey}
                  placeholder={t('AttackGraphTab.searchPlaceholder', 'Find an asset… (Enter to zoom)')}
                  style={{ padding: '6px 10px 6px 30px', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 12.5, width: 180, background: '#fff', outline: 'none' }} />
              </div>
              {layers.map(l => (
                <Chip key={l} active={!hiddenLayers.has(l)} color={LAYER_COLOR[l]} onClick={() => setHiddenLayers(prev => {
                  const next = new Set(prev);
                  if (next.has(l)) next.delete(l); else next.add(l);
                  return next;
                })}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: LAYER_COLOR[l] || '#94a3b8' }} />{l.toLowerCase()}
                </Chip>
              ))}
              <Chip active={riskyOnly} color="#dc2626" onClick={() => setRiskyOnly(v => !v)}>
                <i className="fas fa-fire" style={{ fontSize: 11, color: riskyOnly ? '#dc2626' : '#94a3b8' }} />{t('AttackGraphTab.riskyOnly', 'Risk 50+')}
              </Chip>
            </div>

            {/* right: zoom, fit, minimap, refresh, full screen */}
            <div style={{ ...bar, marginLeft: 'auto', flexShrink: 0, gap: 2 }}>
              <ToolButton icon="fa-minus" title={t('AttackGraphTab.zoomOut', 'Zoom out')} disabled={!isMap} onClick={() => zoomOut({ duration: 200 })} />
              {isMap ? <ZoomLevel /> : <span style={{ minWidth: 44, height: 32 }} />}
              <ToolButton icon="fa-plus" title={t('AttackGraphTab.zoomIn', 'Zoom in')} disabled={!isMap} onClick={() => zoomIn({ duration: 200 })} />
              <ToolButton icon="fa-vector-square" title={t('AttackGraphTab.fitView', 'Fit to screen')} disabled={!isMap} onClick={() => fitView({ duration: 400, padding: 0.12 })} />
              <ToolButton icon="fa-map" title={t('AttackGraphTab.toggleMinimap', 'Show or hide the minimap')} disabled={!isMap} active={isMap && showMinimap} onClick={() => setShowMinimap(v => !v)} />
              <span style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 4px', flexShrink: 0 }} />
              <ToolButton icon="fa-sync" title={t('AttackGraphTab.refreshGraph')} onClick={loadGraph} />
              <ToolButton icon={isFull ? 'fa-compress' : 'fa-expand'} active={isFull} onClick={toggleFull}
                title={isFull ? t('AttackGraphTab.exitFullScreen', 'Exit full screen') : t('AttackGraphTab.fullScreen', 'Full screen')} />
            </div>
          </div>

          {loading ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />{t('AttackGraphTab.buildingGraph')}
            </div>
          ) : raw.nodes.length === 0 ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: '#94a3b8' }}>
              <i className="fas fa-project-diagram" style={{ fontSize: '48px' }} />
              <p style={{ margin: 0 }}>{t('AttackGraphTab.emptyState')}</p>
            </div>
          ) : view === 'radar' ? (
            <div style={{ position: 'absolute', inset: '64px 0 0 0' }}>
              <BlastRadar centerId={center} byId={layout.byId} edges={raw.edges} onSelect={id => { setSelected(id); setRadarCenter(id); }} />
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
              onNodeClick={(_, n) => {
                if (n.type !== 'assetPod' && !n.data?.raw) return;
                if (n.id === selected) setSelected(null); else focus(n.id);
              }}
              onPaneClick={() => setSelected(null)}
              fitView
              fitViewOptions={{ padding: 0.12 }}
              minZoom={0.05}
              attributionPosition="bottom-left"
            >
              <Background color="#cbd5e1" gap={22} size={1.6} />
              {showMinimap && (
                <MiniMap pannable zoomable position="bottom-right"
                  nodeColor={n => (n.type === 'assetPod' ? riskColor(n.data?.riskScore || 0) : 'rgba(148,163,184,0.16)')}
                  nodeStrokeColor={n => (n.type === 'groupBox' ? '#cbd5e1' : 'transparent')}
                  nodeBorderRadius={8}
                  maskColor="rgba(241,245,249,.7)"
                  style={{ background: '#fff' }}
                />
              )}
            </ReactFlow>
          )}
        </div>
        <div style={{ width: 330, flexShrink: 0, height: isFull ? '100%' : '720px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: isFull ? 12 : 20, background: '#fff', padding: 16, boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
          <SidePanel selected={selected} layout={layout} rawEdges={raw.edges} onSelect={focus}
            onRadar={id => { setRadarCenter(id); setView('radar'); }}
            onSimFrom={id => { setSimFrom(id); setSimTo(''); setSimResult(null); }}
            onSimTo={id => { setSimTo(id); setSimResult(null); }} />
        </div>
      </div>

      <div style={{ marginTop: '18px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '18px', boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ width: 30, height: 30, borderRadius: 10, background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-crosshairs" style={{ color: '#fff', fontSize: 13 }} />
          </div>
          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>{t('AttackGraphTab.simulationTitle')}</h4>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#64748b' }}>{t('AttackGraphTab.simulationDescription')}</p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#334155', fontWeight: 600 }}>
              {t('AttackGraphTab.fromAssetLabel')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('AttackGraphTab.fromAssetHint')}</span>
            </label>
            <select value={simFrom} onChange={e => { setSimFrom(e.target.value); setSimTo(''); setSimResult(null); }}
              style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', minWidth: '240px', background: '#fff' }}>
              <option value="">{t('AttackGraphTab.selectSourcePlaceholder')}</option>
              {nodeList.sources.map(n => (
                <option key={n.id} value={n.id}>{n.data?.label} ({n.data?.assetType?.replace(/_/g, ' ')})</option>
              ))}
            </select>
          </div>
          <i className="fas fa-long-arrow-alt-right" style={{ color: '#cbd5e1', fontSize: 18, marginBottom: 10 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#334155', fontWeight: 600 }}>
              {t('AttackGraphTab.toAssetLabel')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('AttackGraphTab.toAssetHint')}</span>
            </label>
            <select value={simTo} onChange={e => setSimTo(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', minWidth: '240px', background: '#fff' }}>
              <option value="">{t('AttackGraphTab.selectTargetPlaceholder')}</option>
              {nodeList.connected.filter(n => String(n.id) !== String(simFrom)).map(n => (
                <option key={n.id} value={n.id}>{n.data?.label} ({n.data?.assetType?.replace(/_/g, ' ')})</option>
              ))}
            </select>
          </div>
          <button onClick={simulate} disabled={!simFrom || !simTo || simLoading}
            style={{
              padding: '9px 22px', background: simFrom && simTo ? '#dc2626' : '#e2e8f0',
              color: simFrom && simTo ? '#fff' : '#94a3b8', border: 'none', borderRadius: '10px',
              cursor: simFrom && simTo ? 'pointer' : 'default', fontSize: '13px', fontWeight: 700,
            }}>
            {simLoading ? <i className="fas fa-spinner fa-spin" /> : t('AttackGraphTab.simulateButton')}
          </button>
        </div>
        {simResult && <SimResult result={simResult} nodeById={layout.byId} onClear={() => setSimResult(null)} />}
      </div>
    </div>
  );
}

export default function AttackGraphTab({ orgId }) {
  return (
    <ReactFlowProvider>
      <AttackGraph orgId={orgId} />
    </ReactFlowProvider>
  );
}
